import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { AppButton } from '@/components'
import { useSellMoneyStore } from '@/store/useSellMoneyStore'
import { useSendConvertOrSell } from '@/domain/transfer/useCases/useSendConvertOrSell'
import { currencyUtils } from '@/utils'

const { parseAmountToFloat, formatDynamicCurrency, formatFiatCurrency } =
  currencyUtils

const MINIMUM_FIAT_AMOUNT = 1.01

export const SellMoneyButtonConfirmation: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const fiatAmount = useSellMoneyStore((state) => state.fiatAmount)
  const cryptoAmount = useSellMoneyStore((state) => state.cryptoAmount)
  const fromCurrency = useSellMoneyStore((state) => state.fromCurrency)
  const toCurrency = useSellMoneyStore((state) => state.toCurrency)
  const calculatedFeeAmount = useSellMoneyStore(
    (state) => state.calculatedFeeAmount,
  )
  const calculatedCreditAmount = useSellMoneyStore(
    (state) => state.calculatedCreditAmount,
  )
  const calculatedTotalDebitAmount = useSellMoneyStore(
    (state) => state.calculatedTotalDebitAmount,
  )
  const hasErrorOnFeeCalculation = useSellMoneyStore(
    (state) => state.hasErrorOnFeeCalculation,
  )

  // Custom toast for insufficient balance
  const showInsufficientBalanceToast = () => {
    toast.error(
      t('api:validationErrors.insufficientBalance', 'Insufficient balance'),
      {
        style: {
          width: 'calc(100vw - 26px)',
          marginLeft: '13px',
          marginRight: '13px',
          maxWidth: 'calc(100vw - 26px)',
        },
        icon: '⚠️',
      },
    )
  }

  const { sendConvertOrSell, isPending: isSending } = useSendConvertOrSell({
    onSuccess: () => {
      // Calculate the fiat amount
      const fiatAmountFloat = parseAmountToFloat({
        amount: fiatAmount.amount,
        decimalSeparator: '.',
      })

      // The credit amount (USDT received) is approximately the fiat value
      // (since USDT is pegged 1:1 to USD)
      const estimatedUsdtAmount =
        fiatAmountFloat / (toCurrency?.pricePerUnit || 1)

      const debitAmountFormatted = formatDynamicCurrency({
        amount: cryptoAmount.amount,
        currencyCode: fromCurrency?.code || 'BTC',
      })

      const creditFiatAmountFormatted = formatFiatCurrency({
        value: fiatAmountFloat,
        fiatCode: fiatAmount.code || 'USD',
        showDecimals: true,
      })

      const paramsData = {
        title: t('translation:conversionSuccessfully.youSold', 'You Sold'),
        amount: String(estimatedUsdtAmount.toFixed(6)),
        subtitle: `${debitAmountFormatted} ${t('translation:conversionSuccessfully.to', 'to')}`,
        code: toCurrency?.code || 'USDT',
        label: toCurrency?.label || '$',
        relativeFiatFormatted: creditFiatAmountFormatted,
      }

      // Navigate to conversion success page
      navigate('/conversion/success', {
        state: paramsData,
      })
    },
    onError: ({ errorMessage }) => {
      // Check if error is about insufficient balance
      if (
        errorMessage?.toLowerCase().includes('insufficient') ||
        errorMessage?.toLowerCase().includes('balance')
      ) {
        showInsufficientBalanceToast()
      } else {
        toast.error(
          errorMessage ||
            t('api:errors.somethingWrong', 'Something went wrong'),
        )
      }
    },
  })

  const validateAndSell = () => {
    if (!fromCurrency || !toCurrency) {
      toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
      return
    }

    if (hasErrorOnFeeCalculation) {
      toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
      return
    }

    const cryptoAmountFloat = parseAmountToFloat({
      amount: cryptoAmount.amount,
      decimalSeparator: '.',
    })

    const fiatAmountFloat = parseAmountToFloat({
      amount: fiatAmount.amount,
      decimalSeparator: '.',
    })

    // Check if amount is greater than 0
    if (cryptoAmountFloat <= 0) {
      toast.error(t('form:errors.amountRequired', 'Please enter an amount'), {
        icon: '⚠️',
      })
      return
    }

    // Check minimum amount
    if (fiatAmountFloat < MINIMUM_FIAT_AMOUNT) {
      const minAmountInCrypto =
        MINIMUM_FIAT_AMOUNT / (fromCurrency.pricePerUnit || 1)
      const label = fromCurrency.displayCurrencyCode || fromCurrency.code
      const message = `${t('translation:min', 'Min')}. ${label} ${t('translation:is', 'is')} ${minAmountInCrypto.toFixed(8)}`

      toast.error(message, {
        style: {
          width: 'calc(100vw - 26px)',
          marginLeft: '13px',
          marginRight: '13px',
          maxWidth: 'calc(100vw - 26px)',
        },
        icon: '⚠️',
      })
      return
    }

    // Check if fee, credit, and total debit amounts are calculated
    if (
      !calculatedFeeAmount ||
      !calculatedCreditAmount ||
      !calculatedTotalDebitAmount
    ) {
      toast.error(t('api:errors.somethingWrong', 'Something went wrong'))
      return
    }

    // Check if user has enough balance for total debit amount (including fee)
    const totalDebitAmountFloat = parseAmountToFloat({
      amount: calculatedTotalDebitAmount,
      decimalSeparator: '.',
    })
    const availableBalance = fromCurrency.amount || 0
    if (totalDebitAmountFloat > availableBalance) {
      showInsufficientBalanceToast()
      return
    }

    // Execute the conversion
    sendConvertOrSell({
      debit_amount: calculatedTotalDebitAmount,
      debit_currency: fromCurrency.code as any,
      credit_amount: calculatedCreditAmount,
      credit_currency: toCurrency.code as any,
      fee_amount: calculatedFeeAmount,
      convert_type: 'sell' as any,
    })
  }

  // Check if button should be disabled
  const isDisabled =
    !fromCurrency || !toCurrency || Number(cryptoAmount.amount || 0) <= 0

  return (
    <AppButton
      label={t('form:actions.sell')}
      onPress={validateAndSell}
      disabled={isDisabled}
      isLoading={isSending}
      type="primaryGradient"
      style={{
        width: '100%',
      }}
    />
  )
}
